import React, { useCallback, useRef, useState } from "react";
import { closeView, viewPositionSelector } from "~redux/slices/edstSlice";
import { useDragging } from "hooks/useDragging";
import {
  useCenterCursor,
  useFocused,
  baseBorderColor,
  colorNameMap,
  usePixiOnClickOutside,
} from "@poscon/shared-frontend";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import { useZIndex } from "hooks/useZIndex";
import { aselFlightplanSelector } from "~redux/slices/aircraftSlice";
import type { ColorSource, Container as PixiContainer } from "pixi.js";
import { edstFontDim } from "~/utils/constants";
import { MenuTitleBar } from "components/utils/MenuTitleBar";
import { InteractiveContainer } from "~/components/utils/InteractiveContainer";
import { EdstButton } from "~/components/utils/EdstButton";
import { EquipmentSurvTemplate } from "./EquipmentSurvTemplate";
import { EquipmentNavTemplate } from "./EquipmentNavTemplate";
import { EquipmentCommTemplate } from "./EquipmentCommTemplate";
import { EquipmentAppServTemplate } from "./EquipmentAppServTemplate";
import { RadioButton, RadioButtonProps } from "../utils/RadioButton";
import { TableField } from "../utils/TableField";
import { EramFlightplan } from "@poscon/shared-types/eram";
import { Nullable } from "@poscon/shared-types";

const { width: fontWidth, height: fontHeight } = edstFontDim;
const width = 100 * fontWidth;
const height = 21 * fontHeight;

const view = "EQUIPMENT_TEMPLATE_MENU";

type MenuOption = "surv" | "nav" | "comm" | "appServ";

type EquipmentTemplateRowProps = {
  x?: number;
  y?: number;
  text: string;
  selected?: boolean;
  onmousedown?: () => void;
  variant?: RadioButtonProps["variant"];
  tint?: ColorSource;
};

export const EquipmentTemplateSelectOption = ({
  x = 0,
  y = 0,
  onmousedown,
  selected = false,
  text,
  variant = "circle",
  tint,
}: EquipmentTemplateRowProps) => {
  return (
    <container x={x} y={y} eventMode="static">
      <RadioButton
        x={4 + Math.floor(edstFontDim.width / 2 + 2)}
        y={2 + Math.floor(edstFontDim.height / 2)}
        size={12}
        borderSize={2}
        borderTint={colorNameMap.mediumGrey}
        selectedTint={colorNameMap.lightGrey}
        selected={selected}
        variant={variant}
        disabled
      />
      <TableField textX={2} x={24} text={text} tint={tint} onmousedown={onmousedown} />
    </container>
  );
};

export type EquipmentTemplateBodyProps = {
  setReset: (callback: () => void) => void;
  fp: Nullable<EramFlightplan>;
};

export const EquipmentTemplateMenu = () => {
  const dispatch = useRootDispatch();
  const pos = useRootSelector((state) => viewPositionSelector(state, view));
  const fp = useRootSelector((state) => aselFlightplanSelector(state));

  const [selectedMenu, setSelectedMenu] = useState<MenuOption>("surv");
  const ref = useRef<PixiContainer>(null);
  const zIndex = useZIndex(view, ref);
  const focused = useFocused(ref);
  useCenterCursor(ref);
  const { startDrag } = useDragging(ref, view, "mouseup");

  usePixiOnClickOutside(ref, () => dispatch(closeView("EQUIPMENT_TEMPLATE_MENU")));

  const resetRef = useRef<() => void>(undefined);

  const setReset = useCallback((callback: () => void) => {
    resetRef.current = callback;
  }, []);

  return (
    <InteractiveContainer ref={ref} x={pos.x} y={pos.y} zIndex={zIndex} sortableChildren>
      <MenuTitleBar
        title={"Equipment Template"}
        width={width}
        withX={false}
        fill={focused ? colorNameMap.cyan : colorNameMap.mediumGrey}
        tint={colorNameMap.black}
        onTitleMouseDown={startDrag}
        view={view}
      />
      <container y={fontHeight + 2}>
        <graphics
          draw={(graphics) => {
            graphics.clear();
            graphics.rect(0, 0, width, height).fill(0).stroke({ width: 1, color: baseBorderColor });
          }}
        />
        <container y={fontHeight + 8}>
          <EdstButton
            x={4}
            width={edstFontDim.width * 5}
            text="SURV"
            selected={selectedMenu === "surv"}
            onmousedown={() => setSelectedMenu("surv")}
          />
          <EdstButton
            x={4 + edstFontDim.width * 5 + 5}
            width={edstFontDim.width * 5}
            text="NAV"
            selected={selectedMenu === "nav"}
            onmousedown={() => setSelectedMenu("nav")}
          />
          <EdstButton
            x={4 + edstFontDim.width * 10 + 10}
            width={edstFontDim.width * 5}
            text="COMM"
            selected={selectedMenu === "comm"}
            onmousedown={() => setSelectedMenu("comm")}
          />
          <EdstButton
            x={4 + edstFontDim.width * 15 + 15}
            text="APP/SERV"
            selected={selectedMenu === "appServ"}
            onmousedown={() => setSelectedMenu("appServ")}
          />
          <EdstButton x={width - edstFontDim.width * 5 - 15} text="RESET" />
        </container>
        <container y={fontHeight * 3 + 10}>
          {selectedMenu === "surv" && <EquipmentSurvTemplate setReset={setReset} fp={fp} />}
          {selectedMenu === "nav" && <EquipmentNavTemplate setReset={setReset} fp={fp} />}
          {selectedMenu === "comm" && <EquipmentCommTemplate setReset={setReset} fp={fp} />}
          {selectedMenu === "appServ" && <EquipmentAppServTemplate setReset={setReset} fp={fp} />}
        </container>
        <container y={height - fontHeight - 13}>
          <graphics
            draw={(graphics) => {
              graphics.clear();
              graphics.moveTo(0, 0).lineTo(width, 0).stroke({ width: 1, color: baseBorderColor });
            }}
          />
          <EdstButton
            x={4}
            y={5}
            text="OK"
            width={6 * edstFontDim.width + 10}
            disabled
            onmousedown={() => {
              // TODO: implement
              dispatch(closeView("EQUIPMENT_TEMPLATE_MENU"));
            }}
          />
          <EdstButton
            x={width - edstFontDim.width * 6 - 15}
            y={5}
            text="Cancel"
            onmousedown={() => dispatch(closeView("EQUIPMENT_TEMPLATE_MENU"))}
          />
        </container>
      </container>
    </InteractiveContainer>
  );
};
